import { api } from "./base";
import { getAttributionForAuth } from "@/shared/lib/attributionUtm";
import { getSessionId, getSessionStartTs } from "@/shared/lib/analyticsSession";
import { getAnyInitData, getInitDataHeaderName, isMiniAppContext, } from "@/shared/lib/miniApp";
import { readVirtuozYclid } from "@/shared/lib/siteAnalyticsStorage";
import { extractSongsBalance } from "@/shared/lib/songsBalance";
import { resolveSiteMetrikaClientId } from "@/shared/lib/yandexMetrikaTagLoader";
import { reportYmHasPaid, setYmUserId, setYmUserParams, } from "@/shared/lib/yandexMetrika";
/**
 * URL ручки бэкенда, инициирующей redirect-OAuth (Яндекс ID / VK ID).
 * Бэкенд кладёт state в httpOnly-cookie, редиректит на провайдера,
 * после успешного OAuth callback ставит JWT-cookies и редиректит на фронт.
 *
 * Используем АБСОЛЮТНЫЙ URL бэкенда — popup открывается напрямую на бэк,
 * куки бэка ставятся на его собственном домене (first-party к бэку),
 * а в главное окно фронта они приезжают автоматически на запросы axios
 * к тому же домену (`withCredentials: true` + CORS на бэке).
 *
 * Если REACT_APP_API_URL не задан, fallback на относительный путь
 * (для случая, когда фронт и бэк на одном домене и nginx сам проксирует).
 */
const YANDEX_LOGIN_PATH = "/api/v1/public/auth/yandex/login";
const VK_LOGIN_PATH = "/api/v1/public/auth/vk/login";
const TELEGRAM_OIDC_PATH = "/api/v1/public/auth/telegram/oidc";
const TELEGRAM_OIDC_ORIGIN = "https://oauth.telegram.org";
/** Оставляем для preload в AuthModal (необязательно для самого login). */
const TELEGRAM_LOGIN_SCRIPT = "https://oauth.telegram.org/js/telegram-login.js";
const buildOAuthLoginUrl = async (loginPath) => {
    const apiUrl = (process.env.REACT_APP_API_URL || "").replace(/\/+$/, "");
    let base;
    if (apiUrl) {
        base = `${apiUrl}${loginPath}`;
    }
    else if (typeof window !== "undefined") {
        base = `${window.location.origin}${loginPath}`;
    }
    else {
        base = loginPath;
    }
    // К ручке OAuth добавляем атрибуцию query-параметрами:
    // - click_id + гранулярные utm_* — атрибуция кампании (см. getAttributionForAuth);
    //   бэк проносит их через OAuth-state и на callback'е синтезирует direct-клик
    //   (если нет click_id) + штампует событие login;
    // - yclid — Yandex ClickID из Директа (?yclid=, readVirtuozYclid);
    // - ycid  — Yandex ClientID из счётчика (ym getClientID);
    // - client_ts + session_id — якорь события login (client_ts — старт сессии;
    //   session_id — UUID гостевой сессии, т.к. на OAuth-редиректе X-Session-Id
    //   не доходит и без него событие «висело» бы вне сессии).
    // yclid и ycid — РАЗНЫЕ идентификаторы. Если значения нет, поле не шлём.
    const params = new URLSearchParams();
    const attribution = getAttributionForAuth();
    for (const [key, value] of Object.entries(attribution)) {
        if (value) {
            params.set(key, value);
        }
    }
    const yclid = readVirtuozYclid();
    if (yclid) {
        params.set("yclid", yclid);
    }
    const ycid = await resolveSiteMetrikaClientId();
    if (ycid) {
        params.set("ycid", ycid);
    }
    params.set("client_ts", getSessionStartTs());
    params.set("session_id", getSessionId());
    const query = params.toString();
    if (!query) {
        return base;
    }
    const separator = base.includes("?") ? "&" : "?";
    return `${base}${separator}${query}`;
};
/**
 * Redirect-OAuth в popup (Яндекс / VK).
 *
 * - Открываем popup СИНХРОННО (в обработчике клика), чтобы не упереться в
 *   блокировщик всплывающих окон, и сразу навигируем его на ручку бэкенда.
 *   URL строим асинхронно: к нему добавляется атрибуция utm/yclid/ycid
 *   (ycid ждёт ym getClientID — поэтому popup открывается на about:blank,
 *   а реальный адрес проставляется, как только URL готов).
 * - Из главного окна поллим `getCurrentUser()`: как только бэк поставит
 *   JWT-куки и `/me` начнёт возвращать 200 — резолвим успех.
 * - Закрываем popup из родительского окна (он наш — `window.open` это разрешает).
 *
 * Резолвится `true` при успешной авторизации,
 * `false` если пользователь сам закрыл popup или его заблокировал браузер.
 */
const loginWithOAuthPopup = (options) => {
    return new Promise((resolve) => {
        if (typeof window === "undefined") {
            resolve(false);
            return;
        }
        const width = 560;
        const height = 720;
        const left = Math.max(0, (window.screen.width - width) / 2);
        const top = Math.max(0, (window.screen.height - height) / 2);
        const popup = window.open("about:blank", options.windowName, `width=${width},height=${height},left=${left},top=${top},menubar=no,toolbar=no,location=no,status=no`);
        if (!popup) {
            console.warn(`${options.windowName} popup blocked by browser`);
            resolve(false);
            return;
        }
        // URL ручки OAuth строим асинхронно (ждём ycid) и навигируем уже
        // открытый popup. window.open отработал синхронно в жесте клика —
        // блокировщик не сработает.
        void buildOAuthLoginUrl(options.loginPath).then((url) => {
            try {
                if (!popup.closed) {
                    popup.location.href = url;
                }
            }
            catch {
                /* после редиректа OAuth окно уже cross-origin — это нормально */
            }
        });
        const POLL_MS = 800;
        const MAX_WAIT_MS = 5 * 60 * 1000;
        const startedAt = Date.now();
        let done = false;
        const finish = (success) => {
            if (done)
                return;
            done = true;
            clearInterval(timer);
            try {
                if (!popup.closed)
                    popup.close();
            }
            catch {
                /* cross-origin: окно мы уже не контролируем — это нормально */
            }
            resolve(success);
        };
        const timer = setInterval(async () => {
            if (Date.now() - startedAt > MAX_WAIT_MS) {
                finish(false);
                return;
            }
            try {
                const user = await getCurrentUser();
                // Гостевой чекаут: /me теперь отдаёт 200 и живому гостю (is_guest=true).
                // Это НЕ успешный вход через соцсеть — иначе поллинг «залогинивал» бы
                // сразу по гостевой сессии, ещё до завершения OAuth. Успех — только
                // когда бэк после callback'а поставил аккаунтовые куки и /me вернул
                // реальный аккаунт (is_guest=false).
                if (user && !user.is_guest) {
                    finish(true);
                    return;
                }
            }
            catch {
                /* валидной сессии нет вовсе — продолжаем поллить */
            }
            if (popup.closed) {
                // Popup закрыт: финальная проверка — вдруг аккаунтовые куки встали прямо
                // перед закрытием (гонка). Иначе считаем, что вход не состоялся.
                try {
                    const user = await getCurrentUser();
                    finish(Boolean(user && !user.is_guest));
                }
                catch {
                    finish(false);
                }
            }
        }, POLL_MS);
    });
};
/** Авторизация через Яндекс ID в popup-окне. */
export const loginWithYandex = () => loginWithOAuthPopup({
    loginPath: YANDEX_LOGIN_PATH,
    windowName: "yandex_oauth",
});
/** Авторизация через VK ID в popup-окне (тот же redirect-flow, что у Яндекса). */
export const loginWithVk = () => loginWithOAuthPopup({
    loginPath: VK_LOGIN_PATH,
    windowName: "vk_oauth",
});
/**
 * Вход через VK ID полностраничным редиректом (без popup'а).
 *
 * Нужен там, где вход стартует НЕ из пользовательского жеста — заход по ссылке
 * из ВК-бота (`?from=vk`, см. useVkEntryAutoLogin): `window.open` вне жеста
 * блокируют браузеры и особенно in-app браузер ВК, поэтому уводим текущую
 * вкладку прямо на ручку OAuth. Бэк после callback'а вернёт на корень сайта
 * (`/?auth=success`), а целевой роут восстановит фронт (см. shared/lib/vkEntry).
 */
export const startVkLoginRedirect = async () => {
    const url = await buildOAuthLoginUrl(VK_LOGIN_PATH);
    window.location.assign(url);
};
let telegramLoginScriptPromise = null;
/**
 * Публичный Client ID бота Telegram OIDC (не секрет).
 * Секрет (Client Secret) живёт только на бэке — во фронт его не кладём.
 */
const getTelegramOidcClientId = () => {
    const raw = (process.env.REACT_APP_TELEGRAM_OIDC_CLIENT_ID || "")
        .trim()
        .replace(/^["']|["']$/g, "");
    if (!raw)
        return null;
    const id = Number(raw);
    return Number.isFinite(id) && id > 0 ? id : null;
};
/**
 * redirect_uri для Telegram OIDC.
 * Должен быть зарегистрирован в BotFather (Login / OIDC callback).
 * По умолчанию стабильный `${origin}/` — не pathname текущей страницы
 * (иначе каждый роут = новый URI, плюс SDK в Mini App иногда уходит в
 * in-app flow без redirect_uri → ошибка «redirect_uri required»).
 */
const getTelegramOidcRedirectUri = () => {
    const fromEnv = (process.env.REACT_APP_TELEGRAM_OIDC_REDIRECT_URI || "")
        .trim()
        .replace(/^["']|["']$/g, "");
    if (fromEnv)
        return fromEnv;
    return `${window.location.origin}/`;
};
/** Подгрузить официальный Telegram Login SDK (опционально, для совместимости). */
export const preloadTelegramLoginScript = () => {
    if (typeof window === "undefined") {
        return Promise.resolve();
    }
    if (window.Telegram?.Login?.auth) {
        return Promise.resolve();
    }
    if (telegramLoginScriptPromise) {
        return telegramLoginScriptPromise;
    }
    telegramLoginScriptPromise = new Promise((resolve, reject) => {
        const existing = document.querySelector(`script[src="${TELEGRAM_LOGIN_SCRIPT}"]`);
        const waitForSdk = () => {
            if (window.Telegram?.Login?.auth) {
                resolve();
                return;
            }
            let attempts = 0;
            const timer = window.setInterval(() => {
                attempts += 1;
                if (window.Telegram?.Login?.auth) {
                    window.clearInterval(timer);
                    resolve();
                    return;
                }
                if (attempts > 50) {
                    window.clearInterval(timer);
                    telegramLoginScriptPromise = null;
                    reject(new Error("Telegram.Login SDK did not initialize"));
                }
            }, 50);
        };
        if (existing) {
            waitForSdk();
            return;
        }
        const script = document.createElement("script");
        script.src = TELEGRAM_LOGIN_SCRIPT;
        script.async = true;
        script.onload = () => waitForSdk();
        script.onerror = () => {
            telegramLoginScriptPromise = null;
            reject(new Error("Failed to load Telegram Login SDK"));
        };
        document.head.appendChild(script);
    });
    return telegramLoginScriptPromise;
};
/**
 * Popup Telegram OIDC с явным redirect_uri + postMessage.
 * Не используем Telegram.Login.auth: в WebView SDK может включить in-app
 * путь без redirect_uri и отдавать «redirect_uri required».
 */
const requestTelegramOidcIdToken = (options) => {
    return new Promise((resolve) => {
        const redirectUri = getTelegramOidcRedirectUri();
        const params = new URLSearchParams({
            response_type: "post_message",
            client_id: String(options.clientId),
            redirect_uri: redirectUri,
            scope: "openid profile telegram:bot_access",
            nonce: options.nonce,
        });
        const authUrl = `${TELEGRAM_OIDC_ORIGIN}/auth?${params.toString()}`;
        const width = 550;
        const height = 650;
        const left = Math.max(0, (window.screen.width - width) / 2) +
            (window.screen.availLeft || 0);
        const top = Math.max(0, (window.screen.height - height) / 2) +
            (window.screen.availTop || 0);
        const popup = window.open(authUrl, "telegram_oidc_login", `width=${width},height=${height},left=${left},top=${top},status=0,location=0,menubar=0,toolbar=0`);
        if (!popup) {
            resolve({ error: "popup_closed" });
            return;
        }
        let done = false;
        const finish = (result) => {
            if (done)
                return;
            done = true;
            window.removeEventListener("message", onMessage);
            window.clearInterval(closeTimer);
            try {
                if (!popup.closed)
                    popup.close();
            }
            catch {
                /* cross-origin */
            }
            resolve(result);
        };
        const onMessage = (event) => {
            if (event.origin !== TELEGRAM_OIDC_ORIGIN)
                return;
            if (event.source !== popup)
                return;
            let data = event.data;
            if (typeof data === "string") {
                try {
                    data = JSON.parse(data);
                }
                catch {
                    return;
                }
            }
            if (!data || typeof data !== "object")
                return;
            const payload = data;
            if (payload.event !== "auth_result")
                return;
            if (payload.error) {
                finish({ error: String(payload.error) });
                return;
            }
            const idToken = payload.result;
            if (typeof idToken !== "string" || !idToken) {
                finish({ error: "missing id_token" });
                return;
            }
            finish({ id_token: idToken });
        };
        window.addEventListener("message", onMessage);
        const closeTimer = window.setInterval(() => {
            if (popup.closed) {
                finish({ error: "popup_closed" });
            }
        }, 200);
        try {
            popup.focus();
        }
        catch {
            /* ignore */
        }
    });
};
/**
 * Авторизация через Telegram OIDC.
 *
 * 1) Popup на oauth.telegram.org с response_type=post_message и явным
 *    redirect_uri → JWT id_token через postMessage.
 * 2) Фронт POSTит id_token (+ nonce + атрибуцию) на
 *    `/api/v1/public/auth/telegram/oidc` — бэк проверяет подпись/JWKS и
 *    ставит JWT-cookies.
 *
 * Резолвится `true` при успехе, `false` если пользователь закрыл popup /
 * браузер заблокировал окно / нет Client ID в env.
 */
export const loginWithTelegramOidc = async () => {
    if (typeof window === "undefined") {
        return false;
    }
    const clientId = getTelegramOidcClientId();
    if (!clientId) {
        console.warn("REACT_APP_TELEGRAM_OIDC_CLIENT_ID is not set");
        return false;
    }
    const nonce = typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
        ? crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    const result = await requestTelegramOidcIdToken({ clientId, nonce });
    if (result.error || !result.id_token) {
        if (result.error === "popup_closed") {
            return false;
        }
        throw new Error(result.error || "Telegram OIDC failed");
    }
    const attribution = getAttributionForAuth();
    const yclid = readVirtuozYclid();
    const ycid = await resolveSiteMetrikaClientId();
    await api.post(TELEGRAM_OIDC_PATH, {
        id_token: result.id_token,
        nonce,
        ...attribution,
        ...(yclid ? { yclid } : {}),
        ...(ycid ? { ycid } : {}),
        client_ts: getSessionStartTs(),
    }, { withCredentials: true });
    return true;
};
/**
 * Потолок ожидания ClientID Метрики для ГОСТЕВОЙ ручки.
 *
 * Гостевая сессия открывается на старте приложения и держит первый экран,
 * поэтому ждать полный резолв (до ~5 с, если тег Метрики режет блокировщик)
 * здесь нельзя — в отличие от register/login, которые идут по клику. Резолвер
 * мемоизирован и стартует параллельно (useServerSiteAnalytics), так что обычно
 * значение уже готово и отдаётся мгновенно.
 */
const GUEST_YCID_WAIT_MS = 400;
/** ycid, если успел резолвиться за GUEST_YCID_WAIT_MS; иначе null. */
const resolveYcidForGuest = async () => Promise.race([
    resolveSiteMetrikaClientId(),
    new Promise((resolve) => {
        setTimeout(() => resolve(null), GUEST_YCID_WAIT_MS);
    }),
]);
export const createGuestSession = async (data) => {
    // Атрибуция гостя: click_id + гранулярные utm_* (см. getAttributionForAuth),
    // yclid (Yandex ClickID) и ycid (Yandex ClientID из счётчика) — как в
    // register/login/oauth; yclid и ycid РАЗНЫЕ идентификаторы. Если есть
    // click_id — бэк берёт UTM с записи клика; если это прямой заход — сам
    // создаёт direct-клик с гранулярными UTM и привязывает гостя.
    // client_ts — якорь события guest_started (старт сессии), чтобы оно встало в
    // таймлайне перед первым page_view. Пустые поля не шлём.
    const attribution = getAttributionForAuth();
    const yclid = readVirtuozYclid();
    const ycid = await resolveYcidForGuest();
    const response = await api.post("/api/v1/public/auth/guest", {
        ...(data ?? {}),
        ...attribution,
        ...(yclid ? { yclid } : {}),
        ...(ycid ? { ycid } : {}),
        client_ts: getSessionStartTs(),
    }, { withCredentials: true });
    return response.data;
};
export const registerUser = async (credentials) => {
    // Атрибуция регистрации: click_id + гранулярные utm_* (см.
    // getAttributionForAuth), yclid (Yandex ClickID из Директа) и ycid (Yandex
    // ClientID из счётчика). yclid и ycid — РАЗНЫЕ идентификаторы. Если значения
    // нет — поле не отправляем.
    const attribution = getAttributionForAuth();
    const yclid = readVirtuozYclid();
    const ycid = await resolveSiteMetrikaClientId();
    await api.post("/api/v1/public/auth/register", {
        email: credentials.email,
        first_name: credentials.first_name,
        last_name: credentials.last_name || null,
        ...attribution,
        ...(yclid ? { yclid } : {}),
        ...(ycid ? { ycid } : {}),
    });
};
export const requestLoginCode = async (credentials) => {
    // Ручка логина на бэке заодно регистрирует пользователя, если его нет
    // (фронт не вызывает /auth/register напрямую). Поэтому прокидываем сюда
    // всю атрибуцию, что есть: click_id + гранулярные utm_* (см.
    // getAttributionForAuth), yclid (Yandex ClickID) и ycid (Yandex ClientID).
    // Если значения нет — поле не отправляем.
    const attribution = getAttributionForAuth();
    const yclid = readVirtuozYclid();
    const ycid = await resolveSiteMetrikaClientId();
    await api.post("/api/v1/public/auth/login", {
        email: credentials.email,
        ...attribution,
        ...(yclid ? { yclid } : {}),
        ...(ycid ? { ycid } : {}),
    });
};
export const verifyCode = async (credentials) => {
    // yclid/ycid — и сюда: атрибуцию СУЩЕСТВУЮЩЕГО пользователя бэк должен
    // обновлять только после подтверждения кода (иначе знающий чужой email
    // перезапишет её запросом /auth/login — риск R5 в metrika_comparison.md).
    // ycid к этому моменту уже в кэше (его ждал запрос кода) — задержки нет.
    const yclid = readVirtuozYclid();
    const ycid = await resolveSiteMetrikaClientId();
    const response = await api.post("/api/v1/public/auth/verify-code", {
        email: credentials.email,
        code: credentials.code,
        // client_ts — якорь события login (email-flow пишется именно здесь, НЕ в
        // /auth/login): старт сессии, чтобы login встал в таймлайне перед
        // page_view. Fallback на серверное время — на стороне бэка.
        client_ts: getSessionStartTs(),
        ...(yclid ? { yclid } : {}),
        ...(ycid ? { ycid } : {}),
    }, {
        withCredentials: true, // Для получения куков
    });
    // Проверяем, что куки установлены (они должны быть в Set-Cookie заголовке ответа)
    // Примечание: браузер автоматически устанавливает куки из Set-Cookie заголовка
    // HttpOnly куки не видны в document.cookie, но они должны отправляться автоматически
    const setCookieHeader = response.headers["set-cookie"];
    if (setCookieHeader) {
        console.log("Cookies set by server (Set-Cookie header):", setCookieHeader);
    }
    else {
        console.warn("No Set-Cookie header in response. Cookies may not be set.");
    }
    console.log("Response status:", response.status);
    console.log("Response headers:", response.headers);
    // Токены приходят в куках, но можем вернуть данные пользователя если они есть
    return response.data;
};
/**
 * Привязать id пользователя к его ClientID Метрики (`setUserID`) — сразу после
 * получения профиля, как только он известен.
 *
 * Нужно офлайн-конверсиям: бэк грузит оплаты по колонке `UserId` со значением
 * `str(user.id)`, и Метрика сматчит их только с теми визиторами, в чьих визитах
 * сайт вызывал setUserID.
 *
 * ГОСТЕЙ пропускаем: их id временный — при логине гость поглощается реальным
 * аккаунтом (absorb_guest) уже с другим id, и связка указывала бы в пустоту.
 * Дедуп на загрузку страницы — внутри setYmUserId.
 *
 * Заодно — параметры посетителя (`userParams`, по образцу VPN/Worldpay):
 * A/B-варианты (их бэк назначает и гостю — сегментация отчётов Метрики по
 * сплитам) и `has_paid`, если бэк его отдаёт (сегмент покупателей для
 * исключения из ретаргетинга). Дедуп — внутри setYmUserParams/reportYmHasPaid.
 */
function reportUserIdToMetrika(userData) {
    const user = userData;
    if (!user) {
        return;
    }
    setYmUserParams({
        frontend_mode: user.frontend_mode,
        paywall_variant: user.paywall_variant,
        welcome_page_variant: user.welcome_page_variant,
    });
    if (user.has_paid === true) {
        reportYmHasPaid();
    }
    if (user.is_guest || user.id == null) {
        return;
    }
    setYmUserId(user.id);
}
export const getCurrentUser = async (initData) => {
    // Если initData не передан, но мы в мини-приложении, пытаемся получить его.
    // Поддерживаем оба моста: Telegram (window.Telegram.WebApp) и MAX
    // (window.WebApp). Имя заголовка зависит от платформы: Telegram →
    // X-Telegram-Init-Data, MAX → X-Max-Init-Data.
    let finalInitData = initData;
    if (!finalInitData && typeof window !== "undefined") {
        finalInitData = getAnyInitData() ?? undefined;
    }
    const initDataHeaderName = getInitDataHeaderName();
    const config = finalInitData
        ? {
            headers: {
                [initDataHeaderName]: finalInitData,
            },
            withCredentials: true,
        }
        : {
            withCredentials: true,
        };
    try {
        // Убеждаемся, что withCredentials включен
        const finalConfig = {
            ...config,
            withCredentials: true,
        };
        // Для веб-версии (не мини-приложение) убеждаемся, что не добавляем Authorization заголовок
        const isMiniApp = isMiniAppContext();
        if (!isMiniApp && finalConfig.headers && "Authorization" in finalConfig.headers) {
            // Удаляем Authorization заголовок для веб-версии, так как токены в куках
            delete finalConfig.headers.Authorization;
        }
        const response = await api.get("/api/v1/public/users/me", finalConfig);
        const userData = response.data;
        // Баланс песен. Новый контракт: `balances: [{type:"songs", amount}]`.
        // Подстраховываемся под разные формы ответа: поле `balances`,
        // legacy-поле `balance`-массивом, либо весь ответ — массив балансов.
        const balanceSource = Array.isArray(userData?.balances)
            ? userData.balances
            : Array.isArray(userData?.balance)
                ? userData.balance
                : Array.isArray(response.data)
                    ? response.data
                    : undefined;
        userData.balance = extractSongsBalance(balanceSource);
        reportUserIdToMetrika(userData);
        return userData;
    }
    catch (error) {
        // Если получили 401 и у нас есть initData, но он не был использован, пробуем с initData
        if (error.response?.status === 401 && finalInitData && !config) {
            try {
                const retryResponse = await api.get("/api/v1/public/users/me", {
                    headers: {
                        [initDataHeaderName]: finalInitData,
                    },
                    withCredentials: true,
                });
                const userData = retryResponse.data;
                // Баланс песен (см. extractSongsBalance) — новый `songs` + legacy.
                userData.balance = extractSongsBalance(Array.isArray(userData?.balances)
                    ? userData.balances
                    : userData?.balance);
                reportUserIdToMetrika(userData);
                return userData;
            }
            catch (retryError) {
                throw error; // Бросаем оригинальную ошибку
            }
        }
        throw error;
    }
};
export const logoutUser = async () => {
    await api.post("/api/v1/public/auth/logout", {}, {
        withCredentials: true, // Для отправки куков
    });
};
/**
 * Переключить current-режим A/B classic/chat.
 * Auth: get_current_user_allow_guest — доступно и гостю.
 * Если frontend_mode ещё не назначен, бэк сначала ставит assigned из сплита 50/50.
 */
export const setFrontendMode = async (mode) => {
    const response = await api.post("/api/v1/public/users/me/frontend-mode", { mode }, { withCredentials: true });
    return response.data;
};
export const refreshAccessToken = async () => {
    await api.post("/api/v1/public/auth/refresh", {}, {
        withCredentials: true, // Для отправки и получения куков
    });
};
export const validateToken = async () => {
    // Проверяем, находимся ли мы в мини-приложении
    const isMiniApp = isMiniAppContext();
    if (isMiniApp) {
        // Для мини-приложения используем токены из localStorage
        const accessToken = localStorage.getItem("accessToken");
        if (!accessToken) {
            throw new Error("Access token is missing");
        }
        try {
            await api.get("/api/v1/user/me");
            return accessToken;
        }
        catch (error) {
            if (error.response?.status === 401) {
                // Для мини-приложения refresh может вернуть токен
                await refreshAccessToken();
                const newToken = localStorage.getItem("accessToken");
                if (newToken) {
                    return newToken;
                }
                throw new Error("Token refresh failed");
            }
            else {
                throw new Error("Token validation failed");
            }
        }
    }
    else {
        // Для веб-версии токены в куках, просто проверяем авторизацию
        try {
            await api.get("/api/v1/public/users/me", {
                withCredentials: true,
            });
            // Возвращаем пустую строку, так как токен в куках
            return "";
        }
        catch (error) {
            if (error.response?.status === 401) {
                // Пытаемся обновить токены через refresh
                await refreshAccessToken();
                // Повторно проверяем
                await api.get("/api/v1/public/users/me", {
                    withCredentials: true,
                });
                return "";
            }
            else {
                throw new Error("Token validation failed");
            }
        }
    }
};
export const updateUserProfile = async (data) => {
    // Новый контракт: PATCH /api/v1/public/users/me (раньше бил в
    // несуществующий PUT /api/v1/user/me). Принимает ТОЛЬКО профильные
    // поля. `email` и `telegram_id` — это identity и меняются отдельными
    // flow'ами (email — через verify-code, привязка платформ — через
    // /link/*); здесь их не шлём — бэк их игнорирует. См. аудит §6.
    const body = {};
    if (data.username !== undefined)
        body.username = data.username;
    if (data.first_name !== undefined)
        body.first_name = data.first_name;
    if (data.last_name !== undefined)
        body.last_name = data.last_name;
    if (data.gender !== undefined)
        body.gender = data.gender;
    if (data.language !== undefined)
        body.language = data.language;
    if (data.billing_email !== undefined)
        body.billing_email = data.billing_email;
    if (data.is_profile_available !== undefined) {
        body.is_profile_available = data.is_profile_available;
    }
    const response = await api.patch("/api/v1/public/users/me", body);
    return response.data;
};
export const getUserByTelegramId = async (telegramId) => {
    const response = await api.get(`/api/v1/public/users/profile/${telegramId}`);
    return response.data;
};
/**
 * Авторизация через Telegram initData
 * В миниаппе авторизация происходит через initData-заголовок (Telegram →
 * X-Telegram-Init-Data, MAX → X-Max-Init-Data) при запросе /api/v1/public/users/me
 */
export const loginWithTelegram = async (initData) => {
    // Получаем пользователя с initData в заголовке
    const user = await getCurrentUser(initData);
    // В реальном API токены могут возвращаться в заголовках или в теле ответа
    // Пока возвращаем только пользователя, токены будут обрабатываться отдельно
    return {
        user,
    };
};
